import { z } from 'zod';
import { PROBLEM_DIFFICULTIES } from './enums.js';
import { SYSTEM_LIMITS } from './limits.js';
import { languageSlugSchema, problemSlugSchema } from './schemas.js';
/**
 * 自動作問 (SPEC 54, 55, 91)。
 *
 * テーマから問題文・想定解・入力生成器を作り、
 * それらを judge sandbox で実行してテストケースを作る。
 *
 * **モデルが書いた「期待される出力」は使わない。**
 * 期待出力は、生成された想定解を実際に実行した結果だけを採用する。
 * モデルの計算を信じると、誰も解けない、あるいは誰でも通る問題ができる。
 */
export const GENERATION_STATUSES = [
    'PENDING',
    'DRAFTING',
    'BUILDING',
    'COMPLETED',
    'FAILED',
];
export const GENERATION_STATUS_LABEL = {
    PENDING: '待機中',
    DRAFTING: '問題案を作成中',
    BUILDING: 'テストケースを生成中',
    COMPLETED: '完了',
    FAILED: '失敗',
};
/** 一度に作るテストケース数の範囲。多すぎると sandbox の実行時間が伸びる。 */
export const GENERATION_LIMITS = {
    MIN_TESTCASES: 3,
    MAX_TESTCASES: 100,
    /** 先頭からこの件数を sample にする。問題ページに出るので小さい入力であること。 */
    SAMPLE_COUNT: 2,
    /** 入力生成器 1 回あたりの実行時間上限。 */
    GENERATOR_TIME_LIMIT_MS: 10_000,
    /**
     * 想定解の実行時間上限。
     * 問題の制限時間そのものではなく、「想定解がここを超えたら問題設定がおかしい」
     * と判断するための上限。
     */
    REFERENCE_TIME_LIMIT_MS: 10_000,
    /** 生成された 1 testcase の入力サイズ上限。 */
    MAX_TESTCASE_INPUT_BYTES: 2 * 1024 * 1024,
};
export const problemGenerationRequestSchema = z.object({
    /** 作りたい問題の方向性。日本語でよい。 */
    theme: z.string().min(1).max(2000),
    slug: problemSlugSchema,
    difficulty: z.enum(PROBLEM_DIFFICULTIES),
    /** 想定解を書かせる言語。 */
    languageSlug: languageSlugSchema,
    testcaseCount: z
        .number()
        .int()
        .min(GENERATION_LIMITS.MIN_TESTCASES)
        .max(GENERATION_LIMITS.MAX_TESTCASES)
        .default(20),
});
/**
 * モデルへ要求する問題案の形。
 *
 * これは構造化出力のスキーマそのものなので、
 * 説明文（describe）はモデルへの指示として機能する。
 */
export const problemDraftSchema = z.object({
    title: z.string().min(1).max(200).describe('問題のタイトル。日本語。'),
    statement: z
        .string()
        .min(1)
        .max(20_000)
        .describe('問題文。Markdown。数式は $...$ の KaTeX 記法。'),
    inputFormat: z.string().max(5_000).describe('入力形式の説明。Markdown。'),
    outputFormat: z.string().max(5_000).describe('出力形式の説明。Markdown。'),
    constraints: z
        .string()
        .max(5_000)
        .describe('制約。Markdown の箇条書き。入力生成器が守る範囲と一致させること。'),
    tags: z
        .array(z.string().max(32))
        .max(5)
        .describe('この問題に当てはまるタグの slug。与えられた一覧の中からのみ選ぶ。'),
    timeLimitMs: z
        .number()
        .int()
        .min(SYSTEM_LIMITS.MIN_TIME_LIMIT_MS)
        .max(SYSTEM_LIMITS.MAX_TIME_LIMIT_MS)
        .describe('実行時間制限（ミリ秒）。想定解が余裕を持って通る値。'),
    memoryLimitMb: z
        .number()
        .int()
        .min(SYSTEM_LIMITS.MIN_MEMORY_LIMIT_MB)
        .max(SYSTEM_LIMITS.MAX_MEMORY_LIMIT_MB)
        .describe('メモリ制限（MB）。'),
    referenceSolution: z
        .string()
        .min(1)
        .max(SYSTEM_LIMITS.MAX_SOURCE_CODE_BYTES)
        .describe('想定解の完全なソースコード。指定された言語で書き、標準入力から読み標準出力へ書く。'),
    generatorSource: z
        .string()
        .min(1)
        .max(SYSTEM_LIMITS.MAX_SOURCE_CODE_BYTES)
        .describe('テスト入力を生成する Python 3 プログラムの完全なソース。' +
        'sys.argv[1] に整数のシードが渡される。' +
        'シードごとに決まった入力を 1 件だけ標準出力へ出す。' +
        'シード 0 と 1 は入出力例として載せるので、人が読める最小限の入力にすること。' +
        'シードが大きいほど制約の上限に近い入力にすること。'),
    editorial: z
        .string()
        .max(20_000)
        .describe('解法の説明。Markdown。計算量にも触れること。'),
});
//# sourceMappingURL=generation.js.map